import React, {useEffect, useState} from "react";
import {Col, Row} from "reactstrap";
import CopyToClipboard from "../../base/copy-to-clipboard";
import IconButton from "../../base/button/icon_button";
import {Divider, SvgIcon} from "@mui/material";
import {ReactComponent as Copy} from "../../../../assets/images/copy.svg";
import Button from "../../base/button/button";
import {formatPhoneNumber, openLinkInNewTab} from "../../../../core/services/utils/utils";
import Dialog from "../../base/dialog";
import {ReactComponent as NameIcon} from "../../../../assets/images/vendor-info/user.svg";
import {ReactComponent as PhoneIcon} from "../../../../assets/images/vendor-info/phone.svg";
import {ReactComponent as EmailIcon} from "../../../../assets/images/vendor-info/email.svg";
import useIsMounted from "../../../hooks/use-is-mounted";
import Api from "../../../../core/services/api/api";
import {Autorenew} from "@mui/icons-material";
import useAccessLevels from "../../../hooks/use-access-levels";

const VendorInformationModal = ({open, setOpen, vendor, setNewLink, request}) => {
    const operatorAccessLevels = useAccessLevels();
    const [resettingLink, setResettingLink] = useState(false);
    const [vendorAccessLink, setVendorAccessLink] = useState(() => request?.vendorAccessLink)
    const isMounted = useIsMounted();

    /**
     * With each change in request:
     * - changes the value of the link.
     */
    useEffect(() => {
        setVendorAccessLink(request?.vendorAccessLink)
    }, [request])

    /**
     * Opens the vendor access link in a new tab
     */
    const openInNewTab = () => {
        openLinkInNewTab(vendorAccessLink);
    }

    /**
     * Resets the access link of the vendor for the given request.
     * If the result of the api is successful, sets the new access link in the internal state.
     */
    const resetLink = () => {
        setResettingLink(true);
        Api.resetWarrantyAndReturnAccessLinkForVendor(request.id).then((response) => {
            if (!isMounted()) return;
            if (response?.resultFlag) {
                const link = response.data;
                setNewLink(request, link);
                setVendorAccessLink(link)
            }
            setResettingLink(false);
        })
    }

    /**
     * Gets users confirmation before removing the current entity.
     */
    const getRemoveConfirmation = () => {
        window.partner?.openConfirmationDialog(
            <div className={'p-3'}>
                <p className={'text-sm text-color-2'}>
                    By resetting this link, the previous link will be un-reachable. Are you sure?
                </p>
            </div>,
            {
                maxWidth: 'sm'
            },
            (confirmed) => confirmed && resetLink(),
        );
    }

    return (
        <Dialog
            open={open}
            setOpen={setOpen}
        >
            <Row className='no-gutters justify-content-center align-items-start w-100'>
                <Row xs={12} className='no-gutters justify-content-center m-3 w-100'>
                    <p className={`text-lg text-color-1 font-weight-semi-bold text-center`}>
                        Vendor Information
                    </p>
                </Row>
                <Divider className='divider'/>
                <Col xs={12} className={'text-center text-lg-start text-break px-4'}>
                    <div
                        className={'vendor-info justify-content-center justify-content-lg-start'}>
                        <NameIcon/>
                        <p className={'title'}>
                            Name:
                        </p>
                        <p className={'value'}>
                            {vendor?.name ?? '--'}
                        </p>
                    </div>
                    <div
                        className={'vendor-info justify-content-center justify-content-lg-start'}>
                        <PhoneIcon/>
                        <p className={'title'}>
                            Phone:
                        </p>
                        <p className={'value'}>
                            {formatPhoneNumber(vendor?.phone)}
                        </p>
                    </div>
                    <div
                        className={'vendor-info justify-content-center justify-content-lg-start'}>
                        <EmailIcon/>
                        <p className={'title'}>
                            Email:
                        </p>
                        <p className={'value'}>
                            {vendor?.email ?? '--'}
                        </p>
                    </div>
                </Col>
                <Col xs={12} className='my-3'>
                    <p className='text-xs secondary-color-1  mb-2 mx-4'>Shareable Link:</p>
                    <Row
                        className={`background-secondary-color-3 rounded-xxl no-gutters align-items-center justify-content-between px-3 py-2 mx-4`}>
                        <Col>
                            <p className='text-color-1 text-sm text-break'>{vendorAccessLink}</p>
                        </Col>
                        <CopyToClipboard>
                            {({copy}) =>
                                <IconButton
                                    onPressed={() => copy(vendorAccessLink)}
                                    color={'paper.secondary'}
                                    className={'copy-to-clipboard-button'}
                                >
                                    <SvgIcon className={'icon normal'} component={Copy}/>
                                </IconButton>
                            }
                        </CopyToClipboard>
                        {
                            operatorAccessLevels.packages.warrantyAndReturn.manageVendor &&
                            <IconButton
                                className={'mx-1 copy-to-clipboard-button'}
                                disabled={resettingLink}
                                onPressed={getRemoveConfirmation}
                                color={'paper.secondary'}
                            >
                                <Autorenew className={'icon normal'}/>
                            </IconButton>
                        }
                    </Row>
                </Col>
                <div className='my-1 w-100'/>
                <Divider className='divider'/>
                <Row className='no-gutters justify-content-between align-items-start w-100'>
                    <Button
                        variant={'dialog'}
                        color={'primary'}
                        onClick={() => setOpen(false)}
                        borderRadius={'0 8px 0 8px'}
                    >
                        Close
                    </Button>
                    <Button
                        variant={'dialog'}
                        color={'primary'}
                        onClick={openInNewTab}
                        borderRadius={'8px 0 8px 0'}
                    >
                        Open in new Tab
                    </Button>
                </Row>
            </Row>
        </Dialog>
    )
}

export default VendorInformationModal;
